import { parseFormLinks } from 'features/geoEvents/geoEvent.utils';
import { differenceWith, isEqual } from 'es-toolkit/compat';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'shared/types';
import { isGeoEventLinkValid } from './useGeoEventLinksFormFieldChanges.utils';

type GeoEventWithLinks = {
  data: {
    links: Link[];
    internalLinks: Link[] | null;
  };
};
type UseGeoEventLinksFormFieldChangesProps = {
  geoEvent: GeoEventWithLinks;
};

type FormValues = {
  links: Link[];
  internalLinks: Link[];
};

export const LINKS_CHANGE_KEYS = [
  'linksTitle',
  'linksAdded',
  'linksRemoved',
] as const;

export const INTERNAL_LINKS_CHANGE_KEYS = [
  'internalLinksTitle',
  'internalLinksAdded',
  'internalLinksRemoved',
] as const;

export type LinksChangeKey = (typeof LINKS_CHANGE_KEYS)[number];
export type InternalLinksChangeKey =
  (typeof INTERNAL_LINKS_CHANGE_KEYS)[number];

export type UseGeoEventLinksFormFieldChangesResult = {
  linksChanges: Partial<Record<LinksChangeKey, string>>;
  internalLinksChanges: Partial<Record<InternalLinksChangeKey, string>>;
};

export const useGeoEventLinksFormFieldChanges = (
  props: UseGeoEventLinksFormFieldChangesProps,
): UseGeoEventLinksFormFieldChangesResult => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  const [links, internalLinks] = useWatch<FormValues>({
    name: ['links', 'internalLinks'],
  }) as [Link[], Link[]];

  const initialLinks = parseFormLinks(geoEvent.data.links);
  const addedLinks = differenceWith(links, initialLinks, isEqual).filter(
    isGeoEventLinkValid,
  );
  const removedLinks = differenceWith(initialLinks, links, isEqual).filter(
    isGeoEventLinkValid,
  );
  // Links is undefined for the report form, but the field is common to wildfire and flooding forms
  const linksChanged =
    Boolean(links) && (addedLinks.length > 0 || removedLinks.length > 0);

  const linksChanges: Partial<Record<LinksChangeKey, string>> = {};
  if (linksChanged) {
    linksChanges.linksTitle = t('geoEventChangelog.links.title');
    if (addedLinks.length > 0) {
      linksChanges.linksAdded = t('geoEventChangelog.links.added', {
        links: addedLinks.map((link) => link.label).join(', '),
      });
    }
    if (removedLinks.length > 0) {
      linksChanges.linksRemoved = t('geoEventChangelog.links.removed', {
        links: removedLinks.map((link) => link.label).join(', '),
      });
    }
  }

  const initialInternalLinks = parseFormLinks(
    geoEvent.data.internalLinks ?? [],
  );
  const addedInternalLinks = differenceWith(
    internalLinks,
    initialInternalLinks,
    isEqual,
  ).filter(isGeoEventLinkValid);
  const removedInternalLinks = differenceWith(
    initialInternalLinks,
    internalLinks,
    isEqual,
  ).filter(isGeoEventLinkValid);
  // Internal links is undefined for the report form, but the field is common to wildfire and flooding forms
  const internalLinksChanged =
    Boolean(internalLinks) &&
    (addedInternalLinks.length > 0 || removedInternalLinks.length > 0);

  const internalLinksChanges: Partial<Record<InternalLinksChangeKey, string>> =
    {};
  if (internalLinksChanged) {
    internalLinksChanges.internalLinksTitle = t(
      'geoEventChangelog.internalLinks.title',
    );
    if (addedInternalLinks.length > 0) {
      internalLinksChanges.internalLinksAdded = t(
        'geoEventChangelog.internalLinks.added',
        {
          links: addedInternalLinks.map((link) => link.label).join(', '),
        },
      );
    }
    if (removedInternalLinks.length > 0) {
      internalLinksChanges.internalLinksRemoved = t(
        'geoEventChangelog.internalLinks.removed',
        {
          links: removedInternalLinks.map((link) => link.label).join(', '),
        },
      );
    }
  }

  return { linksChanges, internalLinksChanges };
};
